/**
 * Product analytics in the app, PostHog EU (issue #15, 2026-09-27).
 *
 * Starts only on app.timefly.no, never locally or in tests, and only with the visitor's "Statistikk"
 * answer in the tf_consent cookie that timefly.no writes on .timefly.no. Autocapture, pageviews,
 * session recording, heatmaps and surveys are off: only the events below are sent. PostHog keeps its
 * anonymous id in a cookie on .timefly.no, so the visit on the site and the sign-up here are one
 * journey; after login the person is identified by the opaque user id, and reset on logout.
 *
 *   signup_started        the first real interaction with Registrer (signup_method)
 *   app_session_started   the app opened, at most once per 30 minutes (workspace_kind, role_category)
 *   onboarding_failed     a confirmation or demo link that did not work (stage, error_code)
 *   signin_method_chosen  a sign-in way picked on Registrer or Logg inn (method: google | microsoft | apple |
 *                         email | password, page: signup | login), #43, 2026-10-07
 *   signin_succeeded      a session from the password form or a provider's link (method), #102
 *   signin_failed         the server's refusal of one (method, reason: the server's code, wrong_password,
 *                         used, ...); the provider refusals only the server sees come from the server
 *   password_reset_requested  Glemt passord sent, no address
 *   error_shown           a refusal shown as a sentence (route pattern, status), from api/client.ts errorText
 *
 * The committed outcomes (signup_completed, demo_ready, business_activated, schedule_saved,
 * assistant_request_completed, the milestones of #102) come from the server, never from here.
 */
import posthog from 'posthog-js';

const KEY = 'phc_vYZtxaF9fRQx7Xw4y6zgF2cFAbeRJPCX6p2zBKbqynBq';
const HOST = 'https://eu.i.posthog.com';
const SESSION_MS = 30 * 60 * 1000;
const UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];

export function analyticsConsent(): boolean {
  return /(?:^|;\s*)tf_consent=v1\.a1\.m[01]/.test(document.cookie);
}

let started = false;
function on(): boolean {
  if (started) return true;
  // TEMPORARY (owner, 2026-10-04, issue #41): on before any choice, off only on an explicit no to
  // Statistikk (tf_consent=v1.a0…); restore with !analyticsConsent()
  if (
    import.meta.env.MODE === 'test' ||
    location.hostname !== 'app.timefly.no' ||
    /(?:^|;\s*)tf_consent=v1\.a0/.test(document.cookie)
  )
    return false;
  posthog.init(KEY, {
    api_host: HOST,
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true, // on only where recordPublicPage() turns it on
    // every text masked in every recording, from the start, not only in a demo: whatever starts or
    // fails to stop a replay, nothing a person typed or reads can be seen in it (security review
    // 2026-10-07, fail-closed). A replay still shows where people go and press; heatmaps are unaffected
    session_recording: {
      maskAllInputs: true,
      maskInputOptions: { password: true, email: true },
      maskTextSelector: '*',
      blockSelector: '.ph-no-capture',
    },
    capture_performance: false, // no web vitals: only the events in the contract (2026-09-28)
    disable_surveys: true,
    // on at init, because set_config cannot start heatmaps later (posthog-js 1.434, #102), and stopped at
    // once below: they run only while measureDemo(true) holds. The project setting must be on too (owner)
    enable_heatmaps: true,
    persistence: 'cookie',
    cross_subdomain_cookie: true,
    person_profiles: 'identified_only',
    // PostHog also keeps the first and the entry URL on its own; a link's token is masked there too
    mask_personal_data_properties: true,
    custom_personal_data_properties: ['token', 'email', 'fbclid', 'gclid', 'next'],
    // no query strings or referrers beyond the four campaign parameters
    sanitize_properties: (props) => {
      for (const k of ['$current_url', '$referrer', '$initial_current_url', '$initial_referrer']) {
        if (typeof props[k] === 'string') props[k] = cleanUrl(props[k]);
      }
      return props;
    },
  });
  heatmaps(false); // fail-closed: nothing is recorded until a demo company is confirmed loaded
  // the owner's browser (#102): every event internal, also before a login and after a logout's reset
  if (OURS.test(document.cookie)) posthog.register({ internal: true });
  started = true;
  return true;
}
/**
 * Heatmaps on or off. set_config alone changes nothing (posthog-js 1.434 has no stop and never re-reads
 * the flag); startIfEnabled is the one switch: it starts when the config says on, and when it says off
 * it clears the timer, removes the listeners and empties the buffer. An explicit flag also wins over
 * the project's remote setting, so nothing can switch them on from outside.
 */
function heatmaps(on: boolean) {
  try {
    posthog.set_config({ enable_heatmaps: on });
    posthog.heatmaps?.startIfEnabled();
  } catch {
    // heatmaps unavailable: the app works the same
  }
}

/** Path and the allowed campaign parameters only; tokens (?token=) and everything else are dropped. */
export function cleanUrl(raw: string): string {
  try {
    const u = new URL(raw);
    const keep = new URLSearchParams();
    for (const k of UTM) {
      const v = u.searchParams.get(k);
      if (v && /^[\w.\-]{1,100}$/.test(v)) keep.set(k, v);
    }
    const q = keep.toString();
    // a «Se selv» code is a link that opens a demo for the address it names: never sent as it is
    const path = u.pathname.replace(/^\/se\/[^/]+/, '/se/:code');
    return `${u.origin}${path}${q ? `?${q}` : ''}`;
  } catch {
    return '';
  }
}

export function track(event: string, properties: Record<string, string | number | boolean | null> = {}) {
  if (on()) posthog.capture(event, properties);
}

/**
 * For a click that leaves the page at once (a provider button goes to Google, Microsoft or Apple): sent
 * by beacon, which the browser finishes after the page is gone; a normal request would be cut off.
 */
export function trackLeaving(
  event: string,
  properties: Record<string, string | number | boolean | null> = {},
) {
  if (on()) posthog.capture(event, properties, { send_instantly: true, transport: 'sendBeacon' });
}

const INTERNAL_USERS = [14, 22]; // the owner's own accounts (#102)
const OUR_COMPANIES = [1, 4]; // the owner's own companies (#102; business_id 1 and 4 in the marketing notes)
const OURS = /(?:^|;\s*)tf_ours=1(?:;|$)/;
/** The owner's own browser (#102): signed in as one of the owner's accounts, or in one of the owner's companies. */
export const isOurs = (userId: number | string, company?: { id?: number } | null) =>
  INTERNAL_USERS.includes(Number(userId)) || OUR_COMPANIES.includes(Number(company?.id));
/**
 * Our own traffic (#102): the owner's accounts and everyone whose company is real (customers and their
 * staff, not prospects) carry `internal: true` on the person and on every event, so PostHog can leave them out.
 */
export const isInternal = (userId: number | string, company?: { id?: number; is_demo?: boolean } | null) =>
  isOurs(userId, company) || (!!company && !company.is_demo);

/**
 * The owner's browser marks itself (#102, fixed 2026-10-14): tf_ours=1 on .timefly.no, so the counts without
 * cookies (backend/services/funnel.js `ours`) skip every beacon from it, on the site and here, and the site's
 * PostHog marks its events internal (site/public/assets/js/consent.js). No identifier, only "this is ours".
 * Set whatever the statistics answer: the counts it fixes run without consent too. Never set for a customer.
 */
function markOurs(userId: number | string, company?: { id?: number } | null) {
  if (!isOurs(userId, company)) return;
  const domain = /(^|\.)timefly\.no$/.test(location.hostname) ? '; domain=.timefly.no' : '';
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `tf_ours=1; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax${domain}${secure}`;
}

/** After login: the opaque user id joins the anonymous journey; the company rides along on every event. */
export function identify(userId: number | string, company?: { id: number; is_demo?: boolean } | null) {
  markOurs(userId, company);
  if (!on()) return;
  const internal = isInternal(userId, company);
  posthog.identify(String(userId), internal ? { internal: true } : undefined);
  if (internal) posthog.register({ internal: true });
  if (company)
    posthog.register({
      business_id: String(company.id),
      workspace_kind: company.is_demo ? 'demo' : 'business',
    });
}

export function sessionStarted(role: string | null, company: { id: number; is_demo?: boolean } | null) {
  if (!on()) return;
  let last = 0;
  try {
    last = Number(localStorage.getItem('tf_session_seen') || 0);
    localStorage.setItem('tf_session_seen', String(Date.now()));
  } catch {
    /* private mode: one event per load is fine */
  }
  if (Date.now() - last < SESSION_MS) return;
  track('app_session_started', {
    workspace_kind: company ? (company.is_demo ? 'demo' : 'business') : null,
    role_category: role === 'admin' ? 'admin' : role ? 'staff' : null,
  });
}

/** Logout: the next person on this browser starts as someone new. */
export function resetAnalytics() {
  if (started) posthog.reset();
}

/**
 * Session replay on the public sign-up and login pages only (owner 2026-10-05: "app.timefly.no does not have
 * replays to see why people don't type in"; Logg inn since #43, 2026-10-07): Registrer and Login. Every input is masked, and the line that
 * repeats the address is marked ph-no-capture. Not on the demo link page or «Se selv»: their address
 * carries a token or a code that opens a demo, and a replay keeps the address as it is. Recording stops when the app itself opens
 * (stopRecording, from Shell): the app holds employee data and is not recorded, except a demo company
 * since #43 (measureDemo).
 */
export function recordPublicPage() {
  if (!on()) return;
  try {
    posthog.startSessionRecording();
  } catch {
    // replay unavailable: the page works the same
  }
}
/**
 * Inside the app: replays and heatmaps for a demo company only (owner 2026-10-07, #43: "need heatmaps").
 * Its people, hours and pay are invented; the visitor's own text is masked, every text in fact.
 * A real company is never recorded and gets no heatmap: we are its processor, and employee pay is on
 * every screen. Fail-closed: the shell calls it with true only while a demo company is confirmed
 * loaded, and with false at every other moment (loading, a switch, an error).
 */
export function measureDemo(isDemo: boolean) {
  if (!on()) return;
  try {
    if (isDemo) {
      // every text masked, not only inputs: the people are invented, but the visitor is not, and their
      // own name, address and questions to Hugin are on screen (security review 2026-10-07). Heatmaps
      // record where people press, not what is written, so they lose nothing
      // all text is masked from init (see the session_recording config above)
      heatmaps(true);
      posthog.startSessionRecording();
    } else {
      heatmaps(false);
      posthog.stopSessionRecording();
    }
  } catch {
    // replay or heatmaps unavailable: the app works the same
  }
}
export function stopRecording() {
  if (!started) return;
  try {
    posthog.stopSessionRecording();
  } catch {
    // nothing recording
  }
}
